import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { MenuTreeItem } from './menu-tree-item';
import { FormatConfigService } from '../format-config.service';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { AppCustomConfigService } from '../app-custom-config.service';
import { MenuItem } from './menu-item';
import { MenuItemRouteMapping } from './menu-item-route-mapping';
import { MenuTreeService } from './menu-tree-service';
import { MenuConfigEntry, MenuConfigResponse } from 'app/core/base-api';

@Injectable()
export class AppMenuService implements OnDestroy {

  private menuSubject = new BehaviorSubject<MenuTreeItem>(null);
  private menuConfigEntries: MenuConfigEntry[];
  private rootMenu: MenuTreeItem;

  public menu$: Observable<MenuTreeItem> = this.menuSubject.asObservable();

  private subscription: Subscription;

  constructor(private formatConfigService: FormatConfigService,
              private appCustomConfigService: AppCustomConfigService,
              private menuTreeService: MenuTreeService) {
    this.subscription = this.formatConfigService.menuConfig$
      .subscribe(menu => this.buildMenu(menu));

    keysToTranslate<string>([
      'core.navigation.abmelden',
      'core.navigation.amtlicheBewertung',
      'core.navigation.berechtigtePersonen',
      'core.navigation.gebaeude',
      'core.navigation.gebaeudeUndWohnungsregister',
      'core.navigation.gemeindeGemeindekreis',
      'core.navigation.geometergeschaeft',
      'core.navigation.grundbuch',
      'core.navigation.grundbuchgeschaeft',
      'core.navigation.grundbuchmeldung',
      'core.navigation.altegrundbuchmeldung',
      'core.navigation.grundstueck',
      'core.navigation.gwrBauprojekte',
      'core.navigation.gwrGebaeude',
      'core.navigation.home',
      'core.navigation.importExport',
      'core.navigation.konfiguration',
      'core.navigation.publikation',
      'core.navigation.rechte',
      'core.navigation.stammdaten',
      'core.navigation.stammpersonen',
      'core.navigation.system',
      'core.navigation.unterstuetzung',
      'core.navigation.unterstuetzungPublic',
      'core.navigation.meineGrundstueckePublic',
      'core.navigation.meineGrundstueckePublic.identifizierungsStufeUnzureichend',
      'core.navigation.meineGrundstueckePublic.stepUpRegistrierung',
      'core.navigation.meineGrundstueckePublic.stepUpRegistrierungLinkText',
      'core.navigation.vermessung',
      'core.navigation.ech0134.grundbuchmeldungen',
      'core.navigation.ech0134.empfaenger',
      'core.navigation.neskoab',
      'core.navigation.impressumRechtliches',
      'core.navigation.glossar'
    ]);
  }

  public buildMenu(menuConfig: MenuConfigResponse) {
    this.menuConfigEntries = menuConfig ? menuConfig.entries : [];
    this.rootMenu = this.menuTreeService.create(this.menuConfigEntries);
    if (!this.menuConfigEntries) {
      return;
    }
    this.initCustomLinkSubscripition();
  }

  private initCustomLinkSubscripition() {
    this.appCustomConfigService.linkConfigSubject.subscribe(
      entries => {
        this.updateSubMenu(entries, MenuItemRouteMapping.unterstuetzungRootItem);
        this.updateSubMenu(entries, MenuItemRouteMapping.unterstuetzungPublicRootItem);
        this.menuSubject.next(this.rootMenu);
      });
  }

  private updateSubMenu(entries, rootMenuItemMapping: MenuItem) {
    const subMenuEntries = entries.filter(value => value.linkRootElement === rootMenuItemMapping.element);
    const rootItem = this.menuConfigEntries.find(c => c.element === rootMenuItemMapping.element);
    if (!subMenuEntries || subMenuEntries.length === 0 || !rootItem) {
      this.rootMenu.removeChild(rootMenuItemMapping.element);
    } else {
      const subMenu = this.menuTreeService.createUpdateableMenu(subMenuEntries, rootItem.orderIndex, rootMenuItemMapping);
      this.rootMenu.replaceChild(rootMenuItemMapping.element, subMenu);
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
